import React, { useState, useEffect } from 'react';
import legalHoldService, { LegalHold, CreateLegalHoldData } from '../../services/legalHoldService';
import {
  ScaleIcon,
  PlusIcon,
  CheckCircleIcon,
  ClockIcon,
  ArrowPathIcon,
  UserIcon,
  CalendarIcon,
  MagnifyingGlassIcon,
} from '@heroicons/react/24/outline';

const AdminLegalHoldsPage: React.FC = () => {
  const [holds, setHolds] = useState<LegalHold[]>([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [showReleaseModal, setShowReleaseModal] = useState(false);
  const [selectedHold, setSelectedHold] = useState<LegalHold | null>(null);
  const [filterStatus, setFilterStatus] = useState<string>('all');
  const [searchQuery, setSearchQuery] = useState('');
  
  const [formData, setFormData] = useState<CreateLegalHoldData>({
    userId: '',
    reason: '',
    caseNumber: '',
    externalReference: '',
    holdType: 'FULL',
  });

  const [releaseReason, setReleaseReason] = useState('');

  useEffect(() => {
    loadHolds();
  }, [filterStatus]);

  const loadHolds = async () => {
    try {
      setLoading(true);
      const params: any = {};
      if (filterStatus !== 'all') {
        params.status = filterStatus.toUpperCase();
      }
      const data = await legalHoldService.getAll(params);
      setHolds(data);
    } catch (error) {
      console.error('Failed to load legal holds:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleCreate = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      await legalHoldService.create(formData);
      setShowModal(false);
      resetForm();
      loadHolds();
    } catch (error) {
      console.error('Failed to create legal hold:', error);
      alert('Failed to create legal hold. Please try again.');
    }
  };

  const handleRelease = async () => {
    if (!selectedHold || !releaseReason) {
      alert('Please provide a release reason.');
      return;
    }
    
    try {
      await legalHoldService.release(selectedHold.id, { releaseReason });
      setShowReleaseModal(false);
      setReleaseReason('');
      setSelectedHold(null);
      loadHolds();
    } catch (error) {
      console.error('Failed to release legal hold:', error);
      alert('Failed to release legal hold. Please try again.');
    }
  };

  const resetForm = () => {
    setFormData({
      userId: '',
      reason: '',
      caseNumber: '',
      externalReference: '',
      holdType: 'FULL',
    });
  };

  const getStatusBadge = (status: string) => {
    if (status === 'ACTIVE') {
      return (
        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-800">
          <ClockIcon className="w-4 h-4 mr-1" />
          Active
        </span>
      );
    }
    return (
      <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
        <CheckCircleIcon className="w-4 h-4 mr-1" />
        Released
      </span>
    );
  };

  const getHoldTypeBadge = (type: string) => {
    const badges: Record<string, { color: string; text: string }> = {
      FULL: { color: 'bg-red-100 text-red-800', text: '🔒 Full Hold' },
      PAYOUT_ONLY: { color: 'bg-orange-100 text-orange-800', text: '💰 Payout Only' },
      CONTENT_ONLY: { color: 'bg-yellow-100 text-yellow-800', text: '📝 Content Only' },
    };
    const badge = badges[type] || badges.FULL;
    return (
      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${badge.color}`}>
        {badge.text}
      </span>
    );
  };

  const filteredHolds = holds.filter(hold => {
    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      return (
        hold.reason.toLowerCase().includes(query) ||
        hold.caseNumber?.toLowerCase().includes(query) ||
        hold.user?.username.toLowerCase().includes(query) ||
        hold.user?.email.toLowerCase().includes(query)
      );
    }
    return true;
  });

  return (
    <div className="min-h-screen bg-gray-50 py-8">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="mb-8">
          <div className="flex items-center justify-between">
            <div className="flex items-center">
              <ScaleIcon className="h-10 w-10 text-purple-600 mr-3" />
              <div>
                <h1 className="text-3xl font-bold text-gray-900">Legal Holds</h1>
                <p className="text-gray-600 mt-1">Manage legal account freezes and restrictions</p>
              </div>
            </div>
            <div className="flex gap-3">
              <button
                onClick={loadHolds}
                className="inline-flex items-center px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50"
              >
                <ArrowPathIcon className="h-4 w-4 mr-2" />
                Refresh
              </button>
              <button
                onClick={() => setShowModal(true)}
                className="inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-purple-600 hover:bg-purple-700"
              >
                <PlusIcon className="h-4 w-4 mr-2" />
                New Hold
              </button>
            </div>
          </div>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-8">
          <div className="bg-white rounded-lg shadow p-6">
            <div className="flex items-center">
              <ScaleIcon className="h-8 w-8 text-gray-400" />
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-600">Total Holds</p>
                <p className="text-2xl font-bold text-gray-900">{holds.length}</p>
              </div>
            </div>
          </div>
          <div className="bg-white rounded-lg shadow p-6">
            <div className="flex items-center">
              <ClockIcon className="h-8 w-8 text-red-500" />
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-600">Active Holds</p>
                <p className="text-2xl font-bold text-red-600">
                  {holds.filter(h => h.status === 'ACTIVE').length}
                </p>
              </div>
            </div>
          </div>
          <div className="bg-white rounded-lg shadow p-6">
            <div className="flex items-center">
              <CheckCircleIcon className="h-8 w-8 text-green-500" />
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-600">Released</p>
                <p className="text-2xl font-bold text-green-600">
                  {holds.filter(h => h.status === 'RELEASED').length}
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Filters */}
        <div className="bg-white rounded-lg shadow mb-6 p-4">
          <div className="flex flex-col sm:flex-row gap-4">
            <div className="flex-1">
              <div className="relative">
                <MagnifyingGlassIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
                <input
                  type="text"
                  placeholder="Search by user, case number, or reason..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                />
              </div>
            </div>
            <div>
              <select
                value={filterStatus}
                onChange={(e) => setFilterStatus(e.target.value)}
                className="px-4 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-purple-500 focus:border-transparent"
              >
                <option value="all">All Status</option>
                <option value="active">Active</option>
                <option value="released">Released</option>
              </select>
            </div>
          </div>
        </div>

        {/* Holds Table */}
        <div className="bg-white rounded-lg shadow overflow-hidden">
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <ArrowPathIcon className="h-8 w-8 text-gray-400 animate-spin" />
              <span className="ml-2 text-gray-600">Loading holds...</span>
            </div>
          ) : filteredHolds.length === 0 ? (
            <div className="text-center py-12">
              <ScaleIcon className="mx-auto h-12 w-12 text-gray-400" />
              <h3 className="mt-2 text-sm font-medium text-gray-900">No legal holds found</h3>
              <p className="mt-1 text-sm text-gray-500">
                {searchQuery ? 'Try adjusting your search criteria' : 'Get started by creating a new legal hold'}
              </p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      User
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Hold Type
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Reason / Case
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Status
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Dates
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {filteredHolds.map((hold) => (
                    <tr key={hold.id} className="hover:bg-gray-50">
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex items-center">
                          <UserIcon className="h-5 w-5 text-gray-400 mr-2" />
                          <div>
                            <div className="text-sm font-medium text-gray-900">
                              {hold.user?.displayName || 'Unknown User'}
                            </div>
                            <div className="text-sm text-gray-500">@{hold.user?.username}</div>
                          </div>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        {getHoldTypeBadge(hold.holdType)}
                      </td>
                      <td className="px-6 py-4">
                        <div className="text-sm text-gray-900">{hold.reason}</div>
                        {hold.caseNumber && (
                          <div className="text-xs text-gray-500 mt-1">
                            Case: {hold.caseNumber}
                          </div>
                        )}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        {getStatusBadge(hold.status)}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        <div className="flex items-center">
                          <CalendarIcon className="h-4 w-4 mr-1" />
                          <div>
                            <div>Start: {new Date(hold.startDate).toLocaleDateString()}</div>
                            {hold.endDate && (
                              <div>End: {new Date(hold.endDate).toLocaleDateString()}</div>
                            )}
                          </div>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm">
                        {hold.status === 'ACTIVE' ? (
                          <button
                            onClick={() => {
                              setSelectedHold(hold);
                              setShowReleaseModal(true);
                            }}
                            className="text-green-600 hover:text-green-900 font-medium"
                          >
                            Release Hold
                          </button>
                        ) : (
                          <button
                            onClick={() => {
                              setSelectedHold(hold);
                              setShowReleaseModal(true);
                            }}
                            className="text-blue-600 hover:text-blue-900 font-medium"
                          >
                            View Details
                          </button>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>

        {/* Create Hold Modal */}
        {showModal && (
          <div className="fixed inset-0 z-50 overflow-y-auto">
            <div className="flex items-center justify-center min-h-screen px-4 pt-4 pb-20 text-center sm:block sm:p-0">
              <div className="fixed inset-0 transition-opacity bg-gray-500 bg-opacity-75" onClick={() => setShowModal(false)} />
              
              <div className="inline-block align-bottom bg-white rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-2xl sm:w-full">
                <form onSubmit={handleCreate}>
                  <div className="bg-white px-4 pt-5 pb-4 sm:p-6 sm:pb-4">
                    <h3 className="text-lg leading-6 font-medium text-gray-900 mb-4">
                      Create Legal Hold
                    </h3>
                    
                    <div className="space-y-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          User ID <span className="text-red-600">*</span>
                        </label>
                        <input
                          type="text"
                          required
                          value={formData.userId}
                          onChange={(e) => setFormData(prev => ({ ...prev, userId: e.target.value }))}
                          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                          placeholder="Enter user ID"
                        />
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Hold Type <span className="text-red-600">*</span>
                        </label>
                        <select
                          value={formData.holdType}
                          onChange={(e) => setFormData(prev => ({ ...prev, holdType: e.target.value as any }))}
                          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                        >
                          <option value="FULL">Full Hold (All account activity)</option>
                          <option value="PAYOUT_ONLY">Payout Only (Block payments)</option>
                          <option value="CONTENT_ONLY">Content Only (Block posting)</option>
                        </select>
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Reason <span className="text-red-600">*</span>
                        </label>
                        <textarea
                          required
                          value={formData.reason}
                          onChange={(e) => setFormData(prev => ({ ...prev, reason: e.target.value }))}
                          rows={3}
                          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                          placeholder="Detailed reason for the legal hold..."
                        />
                      </div>

                      <div className="grid grid-cols-2 gap-4">
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">
                            Case Number
                          </label>
                          <input
                            type="text"
                            value={formData.caseNumber}
                            onChange={(e) => setFormData(prev => ({ ...prev, caseNumber: e.target.value }))}
                            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                            placeholder="e.g., CASE-2024-001"
                          />
                        </div>

                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">
                            External Reference
                          </label>
                          <input
                            type="text"
                            value={formData.externalReference}
                            onChange={(e) => setFormData(prev => ({ ...prev, externalReference: e.target.value }))}
                            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                            placeholder="External ticket/case ID"
                          />
                        </div>
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          End Date (Optional)
                        </label>
                        <input
                          type="date"
                          value={formData.endDate || ''}
                          onChange={(e) => setFormData(prev => ({ ...prev, endDate: e.target.value }))}
                          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                        />
                      </div>
                    </div>
                  </div>
                  
                  <div className="bg-gray-50 px-4 py-3 sm:px-6 sm:flex sm:flex-row-reverse gap-2">
                    <button
                      type="submit"
                      className="w-full inline-flex justify-center rounded-md border border-transparent shadow-sm px-4 py-2 bg-purple-600 text-base font-medium text-white hover:bg-purple-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-purple-500 sm:ml-3 sm:w-auto sm:text-sm"
                    >
                      Create Hold
                    </button>
                    <button
                      type="button"
                      onClick={() => {
                        setShowModal(false);
                        resetForm();
                      }}
                      className="mt-3 w-full inline-flex justify-center rounded-md border border-gray-300 shadow-sm px-4 py-2 bg-white text-base font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-500 sm:mt-0 sm:ml-3 sm:w-auto sm:text-sm"
                    >
                      Cancel
                    </button>
                  </div>
                </form>
              </div>
            </div>
          </div>
        )}

        {/* Release Hold Modal */}
        {showReleaseModal && selectedHold && (
          <div className="fixed inset-0 z-50 overflow-y-auto">
            <div className="flex items-center justify-center min-h-screen px-4 pt-4 pb-20 text-center sm:block sm:p-0">
              <div className="fixed inset-0 transition-opacity bg-gray-500 bg-opacity-75" onClick={() => setShowReleaseModal(false)} />
              
              <div className="inline-block align-bottom bg-white rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-2xl sm:w-full">
                <div className="bg-white px-4 pt-5 pb-4 sm:p-6 sm:pb-4">
                  <h3 className="text-lg leading-6 font-medium text-gray-900 mb-4">
                    {selectedHold.status === 'ACTIVE' ? 'Release Legal Hold' : 'Hold Details'}
                  </h3>
                  
                  <div className="space-y-4">
                    <div className="bg-gray-50 p-4 rounded-md">
                      <div className="grid grid-cols-2 gap-4">
                        <div>
                          <p className="text-sm font-medium text-gray-700">User</p>
                          <p className="text-sm text-gray-900">{selectedHold.user?.displayName}</p>
                          <p className="text-xs text-gray-500">@{selectedHold.user?.username}</p>
                        </div>
                        <div>
                          <p className="text-sm font-medium text-gray-700">Hold Type</p>
                          {getHoldTypeBadge(selectedHold.holdType)}
                        </div>
                        <div>
                          <p className="text-sm font-medium text-gray-700">Case Number</p>
                          <p className="text-sm text-gray-900">{selectedHold.caseNumber || 'N/A'}</p>
                        </div>
                        <div>
                          <p className="text-sm font-medium text-gray-700">Status</p>
                          {getStatusBadge(selectedHold.status)}
                        </div>
                      </div>
                      <div className="mt-4">
                        <p className="text-sm font-medium text-gray-700">Reason</p>
                        <p className="text-sm text-gray-900 mt-1">{selectedHold.reason}</p>
                      </div>
                    </div>

                    {selectedHold.status === 'ACTIVE' && (
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Release Reason <span className="text-red-600">*</span>
                        </label>
                        <textarea
                          value={releaseReason}
                          onChange={(e) => setReleaseReason(e.target.value)}
                          rows={3}
                          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                          placeholder="Explain why the hold is being released..."
                        />
                      </div>
                    )}

                    {selectedHold.releaseReason && (
                      <div className="bg-green-50 p-4 rounded-md">
                        <p className="text-sm font-medium text-green-900">Release Reason</p>
                        <p className="text-sm text-green-700 mt-1">{selectedHold.releaseReason}</p>
                        <p className="text-xs text-green-600 mt-2">
                          Released on {new Date(selectedHold.releasedAt!).toLocaleString()}
                        </p>
                      </div>
                    )}
                  </div>
                </div>
                
                <div className="bg-gray-50 px-4 py-3 sm:px-6 sm:flex sm:flex-row-reverse gap-2">
                  {selectedHold.status === 'ACTIVE' && (
                    <button
                      onClick={handleRelease}
                      disabled={!releaseReason}
                      className="w-full inline-flex justify-center rounded-md border border-transparent shadow-sm px-4 py-2 bg-green-600 text-base font-medium text-white hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500 sm:ml-3 sm:w-auto sm:text-sm disabled:opacity-50"
                    >
                      Release Hold
                    </button>
                  )}
                  <button
                    onClick={() => {
                      setShowReleaseModal(false);
                      setSelectedHold(null);
                      setReleaseReason('');
                    }}
                    className="mt-3 w-full inline-flex justify-center rounded-md border border-gray-300 shadow-sm px-4 py-2 bg-white text-base font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-500 sm:mt-0 sm:ml-3 sm:w-auto sm:text-sm"
                  >
                    {selectedHold.status === 'ACTIVE' ? 'Cancel' : 'Close'}
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default AdminLegalHoldsPage;
